import omit from 'transmute/omit';
import {
  BackendView,
  DEFAULT,
  FORECAST,
  STANDARD,
  View,
  ViewColumn,
  ViewFilterGroupsArray,
  ViewFilterPropertiesArray,
  ViewFiltersArray,
  ViewState,
} from '../types/ViewTypes';
import {
  denormalizeVizType,
  pageTypeToVizType,
  type VizType,
  type PageType,
} from '../../utils/pageTypeVizTypeConversions';

/**
 * Utility that will take a possibly stringified json value, and parse and return it as value of type T
 */
function parseAttribute<T>(value: unknown, defaultValue: T) {
  if (value == null) {
    return defaultValue;
  }

  try {
    if (typeof value === 'string') {
      const val = JSON.parse(value) || defaultValue;
      return val as T;
    } else {
      return value as T;
    }
  } catch (e) {
    return defaultValue;
  }
}

const defaultViewState: ViewState = {
  sortKey: '',
  sortColumnName: '',
  order: 1,
};
const defaultViewColumns: Array<ViewColumn> = [];
const defaultViewFilterGroups: ViewFilterGroupsArray = [];
const defaultViewQuickFilters: ViewFiltersArray = [];
const defaultViewQuickFilterProperties: ViewFilterPropertiesArray = [];

const BACKEND_VIEW_FIELDS_TO_OMIT = [
  'collectionType',
  'portalId',
  'filterGroups',
  'quickFilters',
  'filters',
  'idLabel',
];

const getViewType = (rawView: BackendView): View['type'] => {
  //DEFAULT views do not have a `type` on their response, whereas custom views have type: 'STANDARD' on them
  if (!rawView.type || ![STANDARD, DEFAULT, FORECAST].includes(rawView.type)) {
    return DEFAULT;
  }
  return rawView.type;
};

/**
 * The backend stores columns, state, and filters as stringified json.
 * This function will take a view from the backend and parse out all fields neccessary
 */
export const transformBackendViewToInternalFormat = (
  rawView: BackendView
): View => {
  const baseView = omit<BackendView>(BACKEND_VIEW_FIELDS_TO_OMIT, rawView);
  //sometimes view ids come back as numbers - we want to normalize to strings to prevent 1234 === '1234' mismatches
  const parsedViewId = String(rawView.idLabel || rawView.id);
  const newColumns = parseAttribute<Array<ViewColumn>>(
    rawView.columns,
    defaultViewColumns
  );
  const parsedFilterGroups = parseAttribute<ViewFilterGroupsArray>(
    rawView.filterGroups,
    defaultViewFilterGroups
  );
  const newState = parseAttribute<ViewState>(rawView.state, defaultViewState);

  const newQuickFilters = parseAttribute<ViewFiltersArray>(
    rawView.quickFilters,
    defaultViewQuickFilters
  );

  const newQuickFiltersProperties = parseAttribute<ViewFilterPropertiesArray>(
    rawView.quickFilterProperties,
    defaultViewQuickFilterProperties
  );

  const type = getViewType(rawView);

  // Backend viz type (TABLE) -> internal page type (list) for icons/routing.
  const currentViewVisualization = rawView.currentViewVisualization
    ? denormalizeVizType(rawView.currentViewVisualization as VizType | PageType)
    : rawView.currentViewVisualization;

  return {
    ...baseView,
    type,
    id: parsedViewId,
    columns: newColumns,
    filterGroups: parsedFilterGroups,
    quickFilters: newQuickFilters,
    quickFilterProperties: newQuickFiltersProperties,
    state: newState,
    currentViewVisualization,
  };
};

const propertiesToStringifyIfTheyExist = [
  'state',
  'columns',
  'filters',
  'filterGroups',
  'quickFilters',
  'quickFilterProperties',
];

type ViewProperty = keyof View;

export const convertInternalViewToBEFormat = (
  view: View,
  propertiesToOmitEntirely: ViewProperty[] = []
): BackendView => {
  const viewProperties = Object.keys(view) as ViewProperty[];
  const newView: BackendView = viewProperties.reduce(
    (accumulator, currentValue) => {
      if (propertiesToOmitEntirely.includes(currentValue)) {
        return accumulator;
      } else if (propertiesToStringifyIfTheyExist.includes(currentValue)) {
        const currentval = view[currentValue];
        const stringifiedValue = JSON.stringify(currentval);
        return {
          ...accumulator,
          [currentValue]: stringifiedValue,
        };
      }

      return {
        /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
        ...accumulator,
        [currentValue]: view[currentValue],
      };
    },
    {} as BackendView
  );

  // Internal page type (list) -> backend viz type enum (TABLE) on write.
  if (newView.currentViewVisualization != null) {
    newView.currentViewVisualization = pageTypeToVizType(
      newView.currentViewVisualization as VizType | PageType
    );
  }

  return newView;
};
